import React, { FunctionComponent, useEffect, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

import { fetch_one } from '../../lib/server_helper';
import { checkAllowed } from '../../lib/permission';

import {
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { loginAtom } from '../../lib/auth';

import { AlertCheckInputs } from '../../components/alerts/CheckInputs';
import { AlertSaved } from '../../components/alerts/Saved';
import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import {
  ImagePicker,
  LanguageFab,
  Page,
  StyledButton,
  StyledColourPicker,
  StyledDropzone,
  StyledEditor,
  StyledIconButton,
  StyledSelect,
  StyledSwitch,
  StyledTextField,
  StyledTextFieldTransl,
  Typography
} from '../../components/interface';

import { Company } from '../../models/Company';

// const { DateTime } = require("luxon");

type AvailableLang = 'nl' | 'fr' | 'en';
type Props = {};

export const AdminCompanyDetail: FunctionComponent<Props> = () => {
  const navigate = useNavigate();
  // const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const params = useParams();

  const [login, setLogin] = useAtom(loginAtom);
  const [company, setCompany] = useState<Company>();
  const [loader, setLoader] = useState<boolean>(true);
  const [saveLoader, setSaveLoader] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<string | undefined>(params.id);

  const [item, setItem] = useState<File>();
  const [itemUrl, setItemUrl] = useState<string>();

  const [activeLocale, setActiveLocale] = useState<AvailableLang>('en');

  const [errorSnackOpen, setErrorSnackOpen] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  const [exitSavedSnackOpen, setDividendSavedSnackOpen] = useState<boolean>(false);

  const fields = [
    'id', 'name', 'enabled', 'vat_number', 'vat_amount', 'street', 'zip', 'city', 'country', 'get_item', 'contact_name', 'contact_email', 'is_memoq'
  ];

  const saveSetting = () => {
    if (checkAllowed(login, null, companyId?.toString() || '0', "Company", 'u', true)) {
      setSaveLoader(true);

      let formData = new FormData();
      formData.append('object', 'company');
      formData.append('handler_id', (login?.id || "").toString());
      fields.map(field => {
        formData.append('fields[]', field);
      });
      if (!!companyId) formData.append('ob[id]', companyId.toString());
      if (!!companyId) formData.append('id', (companyId || '').toString());
      // questionDetail
      if (!!item) formData.append(`ob[item]`, item);
      if (!!itemUrl) formData.append(`ob[set_item_url]`, itemUrl);

      formData.append(`ob[name]`, (company?.name || ''));
      formData.append(`ob[vat_number]`, (company?.vat_number?.toString() || ''));
      formData.append(`ob[vat_amount]`, (company?.vat_amount?.toString() || ''));
      formData.append(`ob[street]`, (company?.street || ''));
      formData.append(`ob[zip]`, (company?.zip || ''));
      formData.append(`ob[city]`, (company?.city || ''));
      formData.append(`ob[country]`, (company?.country || ''));
      formData.append(`ob[enabled]`, (!!company?.enabled ? '1' : '0'));
      formData.append(`ob[contact_name]`, (company?.contact_name || ''));
      formData.append(`ob[contact_email]`, (company?.contact_email || ''));
      formData.append(`ob[is_memoq]`, (!!company?.is_memoq ? '1' : '0'));

      fetch_one<Company>('/nl/v3/objects/save', formData, (ob, complete_data) => {
        openObject(ob);
        navigate(`/companies`);
        setSaveLoader(false);
      }, (z) => {
        setServerErrorSnackOpen(true);
      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    }
  };

  const fetchCalculation = () => {
    if (checkAllowed(login, null, companyId?.toString() || '0', "Company", 'r', true)) {
      setLoader(true);
      
      fetch_one<Company>('/nl/v3/objects/fetch_all', {
        object: 'company',
        fields: fields,
        id: companyId
      }, (ob, complete_data) => {
        openObject(ob);
        setLoader(false);
      }, (z) => {
        setServerErrorSnackOpen(true);
      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    } else {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCalculation();
  }, [companyId]);

  const openObject = (ob: Company) => {
    setCompany(ob);
  }

  return (
    <Page sx={{}} title={t("objects.company.title")}>
      <LanguageFab
        activeLocale={activeLocale}
        setActiveLocale={(lc) => {setActiveLocale(lc);}}
      />
      <Grid container spacing="8">

        <Grid item xs={12}>
          <StyledTextField
            label="Name"
            value={company?.name || ''}
            id="name"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0}),
                name: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Contact name"
            value={company?.contact_name || ''}
            id="contact_name"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                contact_name: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Contact email"
            value={company?.contact_email || ''}
            id="contact_email"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                contact_email: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="VAT number"
            value={company?.vat_number || ''}
            id="vat_number"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                vat_number: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="VAT amount"
            value={company?.vat_amount?.toString() || ''}
            id="vat_number"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                vat_amount: !!v ? parseInt(v, 10) : undefined
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            label="Street"
            value={company?.street || ''}
            id="street"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                street: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <StyledTextField
            label="ZIP"
            value={company?.zip || ''}
            id="zip"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                zip: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={8}>
          <StyledTextField
            label="City"
            value={company?.city || ''}
            id="city"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                city: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            label="Country"
            value={company?.country || ''}
            id="country"
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                country: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <StyledSwitch
            label="Enabled"
            value={!!company?.enabled}
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                enabled: !!v
              });
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <StyledSwitch
            label="MEMOQ Client?"
            value={!!company?.is_memoq}
            onChange={(v) => {
              setCompany({
                ...(company || {id: 0, name: ''}),
                is_memoq: !!v
              });
            }}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography sx={{fontSize: '0.8rem'}}>Company logo (200x200)</Typography>
          <ImagePicker existing={company?.get_item} item={item} itemUrl={itemUrl} onSelect={(_item:File|undefined, _itemUrl:string|undefined) => {
            setItem(_item);
            setItemUrl(_itemUrl);
          }} />
        </Grid>

        <Grid item xs={12} sx={{alignContent: 'baseline', textAlign: 'center', position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1201}}>
          <Divider sx={{marginTop: 1, marginBottom: 2}} />

          {!!loader && <CircularProgress />}
          {!loader && <StyledButton
            label={t("public.buttons.cancel")}
            id='cancel'
            // contained
            onClick={(v) => {
              navigate(`/companies`);
            }}
            sx={{marginLeft: 1}}
          />}
          {checkAllowed(login, null, companyId?.toString() || '0', "CompanyTheme", 'u', true) && !loader && <StyledButton
            label={t("public.buttons.save")}
            id='save'
            contained
            onClick={(v) => {
              saveSetting();
            }}
            sx={{marginLeft: 1}}
          />}
        </Grid>
      </Grid>

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
      {!!errorSnackOpen && <AlertCheckInputs open={errorSnackOpen} setOpen={(b) => {setErrorSnackOpen(b);}} />}
      {!!exitSavedSnackOpen && <AlertSaved open={exitSavedSnackOpen} setOpen={(b) => {setDividendSavedSnackOpen(b);}} />}
    </Page>
  );
}
